import apiSlice from '..';
import { TrackingNote, TransferNote, TransferRecord, VehiclePayment } from '../../../pages/Admin/TransferReport/types';
import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/dist/query';

type TransferReportFilterArgs = {
  vin?: string;
  keyword?: string;
  title_inn?: boolean;
  seller_id?: number | null;
  buyer_id?: number | null;
  status?: string[];
  auction_ids?: number[];
  payment_method?: string[];
};

type TransferReportQueryArgs = TransferReportFilterArgs & {
  offset: number;
  limit: number;
  sort_column?: string | null;
  sort_order?: 'asc' | 'desc' | null;
};

type HighlightedCell = {
  column: string | null;
  color: string | null;
};

type TransferReportRow = {
  record: TransferRecord;
  metadata: {
    highlightedCells: HighlightedCell[];
    vehiclePayment: VehiclePayment | null;
  };
};

type TransferReportResult = {
  rows: TransferReportRow[];
  count: number;
};

type TransferReportFilterOption = {
  id: number;
  label: string;
};

type PaymentMethodOption = {
  value: string;
  label: string;
};

export type TransferDocumentLog = {
  id: string;
  action_type: 'file_uploaded' | 'file_removed' | 'file_sent';
  file_name: string;
  file_id: string;
  transfer_record_id: number;
  created_at: string;
  author: { id: number; first_name: string };
  user: {
    id: number;
    dealership_name: string;
    email: string;
  } | null;
};

export type TransferReportLogType =
  | 'payment_method_set'
  | 'title_in_checked'
  | 'money_received_added'
  | 'money_received_updated'
  | 'money_received_deleted'
  | 'funding_complete_checked'
  | 'date_set'
  | 'additional_fee_added'
  | 'additional_fee_updated'
  | 'additional_fee_deleted'
  | 'auto_fee_applied'
  | 'fee_type_edited'
  | 'title_sent'
  | 'title_uploaded'
  | 'title_removed'
  | 'status_changed'
  | 'title_in_unchecked'
  | 'funding_complete_unchecked'
  | 'note_visibility_updated'
  | 'check_updated'
  | 'check_removed'
  | 'tracking_number_added'
  | 'notes_updated'
  | 'tracking_notes_updated'
  | 'misc_fee_applied'
  | 'misc_fee_unapplied';

export type TransferReportLogEntry = {
  id: number;
  type: TransferReportLogType;
  related_item: string | null;
  details:
    | { payment_method: string }
    | { date: string; column: string }
    | { old_status: string; new_status: string }
    | { entity_id: number; amount: number; payment_method: string }
    | { entity_id: number; old_amount: number; new_amount: number }
    | { entity_id: number; deleted_amount: number }
    | { entity_id: number; amount: number; fee_type: string }
    | { entity_id: number; old_fee_type: string; new_fee_type: string }
    | { initial_visibility: { visible_to_seller?: boolean; visible_to_buyer?: boolean } }
    | {
        visibility_removed?: { visible_to_seller?: boolean; visible_to_buyer?: boolean };
        visibility_added?: { visible_to_seller?: boolean; visible_to_buyer?: boolean };
      }
    | {
        buyer_recipients: { dealership_name: string; email: string }[];
        seller_recipients: { dealership_name: string; email: string }[];
      }
    | { old_value: string | null; new_value: string }
    | { old_value: string | null }
    | { value: string }
    | { entity_id: number; description: string; amount: number; user_type: 'buyer' | 'seller'; user_id: number }
    | null;
  created_at: string;
  author: { id: number; first_name: string } | null;
};

export type TransferDocumentQueryResponse = {
  documents: Array<{ id: number; url: string; fileName: string }>;
  logs: TransferDocumentLog[];
  vehicleId: number;
  status: string;
};

export type VinCheckRecord = {
  id: number;
  date_sold: string;
  buyer: string;
  vehicle_price: string;
};

export type VinCheckResult = {
  vehicle_title: string;
  records: VinCheckRecord[];
};

const FLOORING_COMPANY_LABELS: Record<string, string> = {
  next_gear: 'Next Gear',
  afs: 'AFS',
  westlake_flooring: 'Westlake Flooring',
  afi: 'AFI',
  afc: 'AFC',
  kinetic_advantage: 'Kinetic Advantage',
  axle_funding: 'AXLE Funding'
};

function serializePaymentMethod(payment_method: string, flooring_company?: string, other_message?: string): string | null {
  if (payment_method === 'ach') return 'ACH';
  if (payment_method === 'flooring') {
    const company = flooring_company ? (FLOORING_COMPANY_LABELS[flooring_company] ?? flooring_company) : '';
    return `Flooring - ${company}`;
  }
  if (payment_method === 'other') return `Other - ${other_message ?? ''}`;
  if (payment_method === 'on_account') return 'On Account';
  return null;
}

export const transferReportApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getTransferReport: builder.query<TransferReportResult, TransferReportQueryArgs>({
      query: ({
        offset,
        limit,
        vin,
        keyword,
        title_inn,
        seller_id,
        buyer_id,
        status,
        auction_ids,
        payment_method,
        sort_column,
        sort_order
      }) => ({
        method: 'GET',
        url: 'admin/transfer-report',
        params: {
          offset,
          limit,
          ...(vin && { vin }),
          ...(keyword && { keyword }),
          ...(title_inn && { title_inn }),
          ...(seller_id != null && { seller_id }),
          ...(buyer_id != null && { buyer_id }),
          ...(status?.length && { status }),
          ...(auction_ids?.length && { auction_ids }),
          ...(payment_method?.length && { payment_method }),
          ...(sort_column && sort_order && { sort_column, sort_order })
        }
      }),
      transformResponse: (response: any) => ({
        rows: response.data.rows,
        count: response.data.count
      }),
      providesTags: ['TransferReport'],
      keepUnusedDataFor: 0,
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { offset, ...restArgs } = queryArgs;
        return defaultSerializeQueryArgs({ queryArgs: restArgs, endpointDefinition, endpointName });
      },
      merge: (currentCache, newItems, { arg }) => {
        currentCache.count = newItems.count;
        if (arg.offset === 0) {
          currentCache.rows = newItems.rows;
        } else {
          currentCache.rows.push(...newItems.rows);
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.offset === 0 || currentArg?.offset !== previousArg?.offset;
      }
    }),
    updateTransferReport: builder.mutation<void, { id: number; payload: Partial<TransferRecord> }>({
      query: ({ id, payload }) => ({
        method: 'PATCH',
        url: `admin/transfer-record/${id}`,
        body: payload
      })
    }),
    uploadTransferDocument: builder.mutation<void, { id: number; files: File[] }>({
      query: ({ id, files }) => {
        const formData = new FormData();
        files.forEach(file => formData.append('documents', file));
        return {
          method: 'POST',
          url: `admin/transfer-report/${id}/document`,
          body: formData
        };
      },
      invalidatesTags: ['TransferDocuments']
    }),
    getTransferReportSellerOptions: builder.query<TransferReportFilterOption[], TransferReportFilterArgs>({
      query: ({ vin, keyword, title_inn, seller_id, buyer_id, status, auction_ids, payment_method }) => ({
        method: 'GET',
        url: 'admin/transfer-report/seller-options',
        params: {
          ...(vin && { vin }),
          ...(keyword && { keyword }),
          ...(title_inn && { title_inn }),
          ...(seller_id != null && { seller_id }),
          ...(buyer_id != null && { buyer_id }),
          ...(status?.length && { status }),
          ...(auction_ids?.length && { auction_ids }),
          ...(payment_method?.length && { payment_method })
        }
      }),
      transformResponse: (response: any) => response.data
    }),
    getTransferReportBuyerOptions: builder.query<TransferReportFilterOption[], TransferReportFilterArgs>({
      query: ({ vin, keyword, title_inn, seller_id, buyer_id, status, auction_ids, payment_method }) => ({
        method: 'GET',
        url: 'admin/transfer-report/buyer-options',
        params: {
          ...(vin && { vin }),
          ...(keyword && { keyword }),
          ...(title_inn && { title_inn }),
          ...(seller_id != null && { seller_id }),
          ...(buyer_id != null && { buyer_id }),
          ...(status?.length && { status }),
          ...(auction_ids?.length && { auction_ids }),
          ...(payment_method?.length && { payment_method })
        }
      }),
      transformResponse: (response: any) => response.data
    }),
    getTransferDocumentUrl: builder.query<TransferDocumentQueryResponse, number>({
      query: id => ({
        method: 'GET',
        url: `admin/transfer-report/${id}/document-url`
      }),
      transformResponse: (response: any) => response.data,
      providesTags: ['TransferDocuments'],
      keepUnusedDataFor: 0
    }),
    addTransferNote: builder.mutation<
      TransferNote,
      { id: number; payload: { message: string; visible_to_seller: boolean; visible_to_buyer: boolean } }
    >({
      query: ({ id, payload }) => ({
        method: 'POST',
        url: `admin/transfer-record/${id}/notes`,
        body: payload
      }),
      transformResponse: (response: any) => response.data
    }),
    vinCheckTransferReport: builder.query<VinCheckResult, string>({
      query: vin => ({
        method: 'GET',
        url: `admin/transfer-report/vin-check/${vin}`
      }),
      transformResponse: (response: any) => response.data,
      keepUnusedDataFor: 0
    }),
    createTransferReport: builder.mutation<void, { vehicle_id: number }>({
      query: body => ({
        method: 'POST',
        url: 'admin/transfer-report',
        body
      }),
      invalidatesTags: ['TransferReport']
    }),
    getTransferReportPaymentOptions: builder.query<PaymentMethodOption[], void>({
      query: () => ({
        method: 'GET',
        url: 'admin/transfer-report/payment-options'
      }),
      transformResponse: (response: any) => response.data
    }),
    highlightTransferCell: builder.mutation<void, { id: number; color: string | null; column_name: string }>({
      query: ({ id, color, column_name }) => ({
        method: 'POST',
        url: `admin/transfer-record/${id}/highlight`,
        body: { color, column_name }
      })
    }),
    downloadTransferDocuments: builder.mutation<Blob, { selections: Array<{ fileId: number; pages?: number[] }> }>({
      query: ({ selections }) => ({
        method: 'POST',
        url: 'admin/transfer-report/documents/download',
        body: { files: selections },
        responseHandler: (response: Response) => response.blob()
      })
    }),
    sendTransferDocuments: builder.mutation<
      void,
      { to: string[]; notes: string; files: Array<{ fileId: number; pages?: number[] }> }
    >({
      query: body => ({
        method: 'POST',
        url: 'admin/transfer-report/documents/send',
        body
      })
    }),
    getVehicleContacts: builder.query<{ emails: string[] }, { vehicleId: number; userType: 'buyer' | 'seller' }>({
      query: ({ vehicleId, userType }) => ({
        method: 'GET',
        url: `vehicles/${vehicleId}/contacts`,
        params: { userType }
      }),
      transformResponse: (response: any) => response.data,
      keepUnusedDataFor: 0
    }),
    deleteTransferDocument: builder.mutation<void, { fileId: number }>({
      query: ({ fileId }) => ({
        method: 'DELETE',
        url: `admin/transfer-report/document/${fileId}`
      }),
      invalidatesTags: ['TransferDocuments']
    }),
    getBuyerTransferDocumentUrl: builder.query<TransferDocumentQueryResponse, number>({
      query: id => ({
        method: 'GET',
        url: `buyer/transfer-report/${id}/document-url`
      }),
      transformResponse: (response: any) => response.data,
      keepUnusedDataFor: 0
    }),
    updateTransferNote: builder.mutation<
      void,
      { noteId: number; payload: { visible_to_buyer: boolean; visible_to_seller: boolean } }
    >({
      query: ({ noteId, payload }) => ({
        method: 'PATCH',
        url: `admin/transfer-report/notes/${noteId}`,
        body: payload
      })
    }),
    updateTransferNoteMessage: builder.mutation<TransferNote, { noteId: number; payload: { message: string } }>({
      query: ({ noteId, payload }) => ({
        method: 'PATCH',
        url: `admin/transfer-report/notes/${noteId}`,
        body: payload
      }),
      transformResponse: (response: any) => response.data
    }),
    deleteTransferNote: builder.mutation<void, { noteId: number }>({
      query: ({ noteId }) => ({
        method: 'DELETE',
        url: `admin/transfer-report/notes/${noteId}`
      })
    }),
    deleteTrackingNote: builder.mutation<void, { noteId: number }>({
      query: ({ noteId }) => ({
        method: 'DELETE',
        url: `admin/transfer-record/tracking-notes/${noteId}`
      })
    }),
    addTrackingNote: builder.mutation<TrackingNote, { id: number; payload: { message: string } }>({
      query: ({ id, payload }) => ({
        method: 'POST',
        url: `admin/transfer-record/${id}/tracking-notes`,
        body: payload
      }),
      transformResponse: (response: any) => response.data
    }),
    updateTrackingNote: builder.mutation<TrackingNote, { noteId: number; payload: { message: string } }>({
      query: ({ noteId, payload }) => ({
        method: 'PATCH',
        url: `admin/transfer-record/tracking-notes/${noteId}`,
        body: payload
      }),
      transformResponse: (response: any) => response.data
    }),
    getTransferReportLogs: builder.query<TransferReportLogEntry[], number>({
      query: id => ({
        method: 'GET',
        url: `admin/transfer-record/${id}/logs`
      }),
      transformResponse: (response: { success: boolean; data: TransferReportLogEntry[] }) => response.data,
      providesTags: ['TransferReportLogs'],
      keepUnusedDataFor: 0
    }),
    updateVehiclePaymentMethod: builder.mutation<
      void,
      { vehicleId: number; payment_method: string; flooring_company?: string; other_message?: string }
    >({
      query: ({ vehicleId, payment_method, flooring_company, other_message }) => ({
        method: 'POST',
        url: `admin/vehicles/${vehicleId}/payment-update`,
        body: {
          payment_method,
          ...(flooring_company && { flooring_company }),
          ...(other_message && { other_message })
        }
      }),
      async onQueryStarted({ vehicleId, payment_method, flooring_company, other_message }, { dispatch, queryFulfilled, getState }) {
        const queries = (getState() as any)['api']?.queries ?? {};
        const patches: Array<{ undo: () => void }> = [];

        for (const entry of Object.values(queries) as any[]) {
          if (entry?.endpointName !== 'getTransferReport' || !entry?.data?.rows) continue;
          const patch = dispatch(
            transferReportApiSlice.util.updateQueryData('getTransferReport', entry.originalArgs, draft => {
              const row = draft.rows.find((r: any) => r.record.vehicle_id === vehicleId);
              if (!row) return;
              row.record.buyers_payment_method = serializePaymentMethod(payment_method, flooring_company, other_message) ?? payment_method;
              row.metadata.vehiclePayment = {
                ...row.metadata.vehiclePayment,
                payment_method: null,
                payment_type: payment_method,
                payment_company: flooring_company ?? null,
                payment_comment: other_message ?? null
              };
            })
          );
          patches.push(patch);
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach(p => p.undo());
        }
      }
    })
  }),
  overrideExisting: true
});

export const {
  useSendTransferDocumentsMutation,
  useLazyGetVehicleContactsQuery,
  useGetTransferReportQuery,
  useUpdateTransferReportMutation,
  useUploadTransferDocumentMutation,
  useGetTransferReportSellerOptionsQuery,
  useGetTransferReportBuyerOptionsQuery,
  useGetTransferDocumentUrlQuery,
  useAddTransferNoteMutation,
  useAddTrackingNoteMutation,
  useUpdateTrackingNoteMutation,
  useLazyVinCheckTransferReportQuery,
  useCreateTransferReportMutation,
  useGetTransferReportPaymentOptionsQuery,
  useHighlightTransferCellMutation,
  useDownloadTransferDocumentsMutation,
  useDeleteTransferDocumentMutation,
  useUpdateTransferNoteMutation,
  useUpdateTransferNoteMessageMutation,
  useDeleteTransferNoteMutation,
  useDeleteTrackingNoteMutation,
  useGetBuyerTransferDocumentUrlQuery,
  useGetTransferReportLogsQuery,
  useUpdateVehiclePaymentMethodMutation
} = transferReportApiSlice;
export default transferReportApiSlice;
